import { Injectable } from '@angular/core';

export type FontSizeScale = 'small' | 'default' | 'large' | 'xlarge';
export type FontWeightScale = 'light' | 'default' | 'bold';
export type DensityScale = 'compact' | 'default' | 'spacious';
export type CornerRadiusScale = 'sharp' | 'default' | 'round';
export type FontFamilyKey = 'inter' | 'roboto' | 'poppins' | 'open-sans' | 'nunito';
export type ThemePresetKey =
  'vivi-red' | 'ocean-blue' | 'forest-green' | 'sunset-orange' | 'royal-purple' | 'midnight-dark';

export interface AppearanceSettings {
  fontFamily: FontFamilyKey;
  fontSize: FontSizeScale;
  fontWeight: FontWeightScale;
  density: DensityScale;
  cornerRadius: CornerRadiusScale;
  theme: ThemePresetKey;
  tableRowHeight: number;
  cellPaddingX: number;
}

export interface ThemePresetDef {
  key: ThemePresetKey;
  label: string;
  /** Primary, secondary, accent, background, text — the picker's swatch row. */
  swatches: [string, string, string, string, string];
  /** Dark presets also get `app-theme-dark`, which the chrome fix-ups key on. */
  dark: boolean;
}

/**
 * Colour presets. Each one has a matching `body.app-theme-<key>` token block
 * in styles.scss; the swatches here are only what the card renders.
 * Vivi Red is the shipped default and lives in the bare `body` block.
 */
export const THEME_PRESETS: ThemePresetDef[] = [
  {
    key: 'vivi-red',
    label: 'Vivi Red',
    swatches: ['#b31d2c', '#fdf2f3', '#efece8', '#faf8f6', '#1f1617'],
    dark: false,
  },
  {
    key: 'ocean-blue',
    label: 'Ocean Blue',
    swatches: ['#2563eb', '#93c5fd', '#e0f2fe', '#f3f4f6', '#0b1f3b'],
    dark: false,
  },
  {
    key: 'forest-green',
    label: 'Forest Green',
    swatches: ['#16a34a', '#34d399', '#dcfce7', '#fffbf7', '#2f241e'],
    dark: false,
  },
  {
    key: 'sunset-orange',
    label: 'Sunset Orange',
    swatches: ['#f97316', '#fb923c', '#fde68a', '#fffbeb', '#1f1617'],
    dark: false,
  },
  {
    key: 'royal-purple',
    label: 'Royal Purple',
    swatches: ['#7c3aed', '#a78bfa', '#ede9fe', '#f5f3ff', '#2e1065'],
    dark: false,
  },
  {
    key: 'midnight-dark',
    label: 'Midnight Dark',
    swatches: ['#0f172a', '#1e293b', '#14b8a6', '#111827', '#ffffff'],
    dark: true,
  },
];

export interface FontFamilyDef {
  key: FontFamilyKey;
  label: string;
  /** CSS font-family name. */
  css: string;
  /** Google Fonts css2 family query (null = already bundled). */
  google: string | null;
}

/** The five families the picker offers. Inter is bundled by styles.scss. */
export const FONT_FAMILIES: FontFamilyDef[] = [
  { key: 'inter', label: 'Inter', css: 'Inter', google: null },
  { key: 'roboto', label: 'Roboto', css: 'Roboto', google: 'Roboto:wght@100..900' },
  {
    key: 'poppins',
    label: 'Poppins',
    css: 'Poppins',
    google: 'Poppins:wght@100;300;400;500;600;700;800;900',
  },
  { key: 'open-sans', label: 'Open Sans', css: 'Open Sans', google: 'Open+Sans:wght@300..800' },
  { key: 'nunito', label: 'Nunito', css: 'Nunito', google: 'Nunito:wght@200..1000' },
];

const STORAGE_KEY = 'appearance';

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  fontFamily: 'inter',
  fontSize: 'default',
  fontWeight: 'default',
  density: 'default',
  cornerRadius: 'default',
  theme: 'vivi-red',
  tableRowHeight: 48,
  cellPaddingX: 16,
};

/** Base font size per scale; zoom is derived relative to the 14px default. */
export const FONT_SIZE_PX: Record<FontSizeScale, number> = {
  small: 12,
  default: 14,
  large: 16,
  xlarge: 18,
};

/** Pre-preset values that may still sit in a browser's localStorage. */
const LEGACY_THEMES: Record<string, ThemePresetKey> = {
  light: 'vivi-red',
  system: 'vivi-red',
  dark: 'midnight-dark',
};

const LEGACY_RADII: Record<string, CornerRadiusScale> = {
  0: 'sharp',
  4: 'sharp',
  8: 'default',
  16: 'round',
  32: 'round',
};

const LEGACY_FONTS: Record<string, FontFamilyKey> = {
  lexend: 'inter',
  jakarta: 'poppins',
  'dm-sans': 'open-sans',
  manrope: 'nunito',
};

/**
 * Per-device appearance preferences (colour preset, typography, density,
 * table spacing). Stored in localStorage and applied as classes/styles on
 * <body>: the preset drives the --c-* palette, the weight scale the --fw-*
 * tokens, and the table spacing --app-row-h / --app-cell-px.
 */
@Injectable({ providedIn: 'root' })
export class AppearanceService {
  private saved: AppearanceSettings = this.load();
  private appliedZoom = 1;

  get current(): AppearanceSettings {
    return { ...this.saved };
  }

  preset(key: ThemePresetKey): ThemePresetDef {
    return THEME_PRESETS.find((p) => p.key === key) || THEME_PRESETS[0];
  }

  init() {
    this.apply(this.saved);
    // vh units don't rescale under zoom, so layout heights use --app-vhz,
    // which must track window resizes.
    window.addEventListener('resize', () => this.updateViewportVar());
  }

  /** Apply without persisting — used for the settings screen's live preview. */
  preview(settings: AppearanceSettings) {
    this.apply(settings);
  }

  save(settings: AppearanceSettings) {
    this.saved = { ...settings };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.saved));
    this.apply(this.saved);
  }

  reset() {
    this.saved = { ...DEFAULT_APPEARANCE };
    localStorage.removeItem(STORAGE_KEY);
    this.apply(this.saved);
  }

  /** Re-apply the persisted settings, discarding any unsaved preview. */
  revertPreview() {
    this.apply(this.saved);
  }

  private load(): AppearanceSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = { ...DEFAULT_APPEARANCE, ...JSON.parse(raw) };
        if (!(parsed.fontSize in FONT_SIZE_PX)) {
          parsed.fontSize = 'default';
        }
        if (parsed.fontFamily in LEGACY_FONTS) {
          parsed.fontFamily = LEGACY_FONTS[parsed.fontFamily];
        }
        if (!FONT_FAMILIES.some((f) => f.key === parsed.fontFamily)) {
          parsed.fontFamily = DEFAULT_APPEARANCE.fontFamily;
        }
        if (parsed.cornerRadius in LEGACY_RADII) {
          parsed.cornerRadius = LEGACY_RADII[parsed.cornerRadius];
        }
        if (!['sharp', 'default', 'round'].includes(parsed.cornerRadius)) {
          parsed.cornerRadius = DEFAULT_APPEARANCE.cornerRadius;
        }
        if (parsed.theme in LEGACY_THEMES) {
          parsed.theme = LEGACY_THEMES[parsed.theme];
        }
        if (!THEME_PRESETS.some((p) => p.key === parsed.theme)) {
          parsed.theme = DEFAULT_APPEARANCE.theme;
        }
        return parsed;
      }
    } catch {
      /* corrupted value — fall back to defaults */
    }
    return { ...DEFAULT_APPEARANCE };
  }

  private apply(settings: AppearanceSettings) {
    const body = document.body;
    const preset = this.preset(settings.theme);

    Array.from(body.classList)
      .filter(
        (cls) =>
          cls.startsWith('app-weight-') ||
          cls.startsWith('app-density-') ||
          cls.startsWith('app-radius-') ||
          cls.startsWith('app-theme-'),
      )
      .forEach((cls) => body.classList.remove(cls));
    body.classList.add(`app-weight-${settings.fontWeight}`);
    body.classList.add(`app-density-${settings.density}`);
    body.classList.add(`app-radius-${settings.cornerRadius}`);
    body.classList.add(`app-theme-${preset.key}`);
    if (preset.dark) {
      // What the Bootstrap/Material chrome fix-ups in styles.scss key on.
      body.classList.add('app-theme-dark');
    }

    const zoom = FONT_SIZE_PX[settings.fontSize] / FONT_SIZE_PX.default;
    (body.style as any).zoom = String(zoom);
    // Body-attached overlay roots (Material dialogs, toasts) counter-zoom
    // with this so 100vw/100vh dialogs still cover the real viewport.
    body.style.setProperty('--app-zoom-inv', String(1 / zoom));
    this.appliedZoom = zoom;
    this.updateViewportVar();
    body.style.setProperty('--app-row-h', `${settings.tableRowHeight}px`);
    body.style.setProperty('--app-cell-px', `${settings.cellPaddingX}px`);

    const family = FONT_FAMILIES.find((f) => f.key === settings.fontFamily) || FONT_FAMILIES[0];
    this.ensureFontLoaded(family);
    body.style.setProperty('--app-font', `'${family.css}'`);
    // Several hundred component rules still name the pre-picker default
    // literally, so the override has to be on for every family, Inter
    // included — not just the non-default ones.
    body.classList.add('app-font-override');
  }

  /**
   * Zoom-corrected viewport height: a css-px length that renders exactly one
   * viewport tall inside the zoomed subtree (100vh does not — it leaves a gap
   * below 100% and overflows above it).
   */
  private updateViewportVar() {
    document.body.style.setProperty('--app-vhz', `${window.innerHeight / this.appliedZoom}px`);
  }

  /** Lazily add the Google Fonts stylesheet for a family, once. */
  ensureFontLoaded(family: FontFamilyDef) {
    if (!family.google) {
      return;
    }
    const id = `app-font-${family.key}`;
    if (document.getElementById(id)) {
      return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${family.google}&display=swap`;
    document.head.appendChild(link);
  }
}
